import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useSEO } from "@/hooks/useSEO";
import {
  Loader, Breadcrumbs, SectionRenderer, IntelligenceBox, KeyTakeaways, FAQ,
  ArticleCard, ToolCard, Overline, LazyImage, Meta, formatDate,
} from "@/components/shared";
import { NotFound } from "@/pages/ToolDetail";
import { AdSlot } from "@/components/Ads";
import { Clock, Calendar, RefreshCw, ExternalLink } from "lucide-react";

export default function Article() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => { setD(null); api.article(slug).then(setD).catch(() => setD(false)); }, [slug]);
  useEffect(() => { if (d && d.article) track.articleView(d.article); }, [d]);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement;
      const p = (h.scrollTop) / (h.scrollHeight - h.clientHeight);
      setProgress(Math.min(100, Math.max(0, p * 100)));
    };
    window.addEventListener("scroll", on); return () => window.removeEventListener("scroll", on);
  }, []);
  useSEO(d && d.article ? { title: d.article.seo_title, description: d.article.meta_description } : {});
  if (d === false) return <NotFound />;
  if (!d) return <Loader />;
  const a = d.article;

  return (
    <div>
      <div className="fixed top-0 left-0 h-1 bg-klyroo-cyan z-[60] transition-[width]" style={{ width: `${progress}%` }} />
      <div className="klyroo-grid border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: a.category, to: `/${a.category.toLowerCase()}` }, { label: a.subcategory || a.content_type }]} />
          <Overline>{a.category} · {a.content_type}</Overline>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mt-3 leading-[1.08]">{a.title}</h1>
          <p className="text-slate-300 mt-4 text-lg sm:text-xl leading-relaxed">{a.subtitle}</p>
          <div className="flex flex-wrap items-center gap-4 mt-6 text-slate-400">
            <span className="text-sm font-medium text-white">{a.author}</span>
            <Meta><Calendar size={12} /> {formatDate(a.published_at)}</Meta>
            <Meta><RefreshCw size={12} /> Updated {formatDate(a.updated_at)}</Meta>
            <Meta><Clock size={12} /> {a.reading_time} min read</Meta>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10">
        <div className="max-w-3xl w-full min-w-0">
        <figure className="mb-3">
          <div className="rounded-2xl overflow-hidden"><LazyImage src={a.image} alt={a.image_alt} ratio="16/9" /></div>
          <figcaption className="text-xs font-mono text-slate-500 mt-2">{a.image_alt} · {a.image_credit}</figcaption>
        </figure>

        <div className="flex flex-wrap gap-2 my-6">
          {a.tags.map((t) => <span key={t} className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300">#{t}</span>)}
        </div>

        <KeyTakeaways items={a.key_takeaways} />
        <IntelligenceBox intel={a.intelligence} />
        <SectionRenderer sections={a.body_sections} />
        {(a.cta_buttons || []).length > 0 && (
          <div className="flex flex-wrap gap-3 my-8" data-testid="article-cta-buttons">
            {a.cta_buttons.map((c, i) => (
              <a key={i} href={c.url} target="_blank" rel="sponsored noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-6 py-3 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white transition-colors">
                {c.text || "Learn more"} <ExternalLink size={15} />
              </a>
            ))}
          </div>
        )}
        <FAQ faqs={a.faqs} />
        </div>
        <aside className="lg:sticky lg:top-24 h-fit"><AdSlot placement="sidebar" /></aside>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        {d.related_tools?.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Related Tools</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{d.related_tools.map((t) => <ToolCard key={t.slug} t={t} />)}</div>
          </section>
        )}
        {d.related_articles?.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Related Stories</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{d.related_articles.slice(0, 3).map((r) => <ArticleCard key={r.slug} a={r} />)}</div>
          </section>
        )}
        {d.related_topics?.length > 0 && (
          <section data-testid="related-topics">
            <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Related Topics</h2>
            <div className="flex flex-wrap gap-2">
              {d.related_topics.map((t) => (
                <span key={t} data-testid={`related-topic-${t}`} className="px-3 py-1.5 rounded-lg bg-white/5 text-slate-300 text-sm font-mono">#{t}</span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
